import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { WorkoutDialog } from "./dialogs/WorkoutDialog.tsx";
import { RoutineDialog } from "./dialogs/RoutineDialog.tsx";
import { SetDialog } from "./dialogs/SetDialog.tsx";
import { ExerciseDialog } from "./dialogs/ExerciseDialog.tsx";
import { ProfileDialog } from "./dialogs/ProfileDialog.tsx";
import { ConfirmDialog } from "./dialogs/ConfirmDialog.tsx";
import { User } from "../api/types.ts";
import { MergeExerciseDialog } from "./dialogs/MergeExerciseDialog.tsx";
import {
  OnboardingDialog,
  OnboardingDialogStep,
} from "./dialogs/OnboardingDialog.tsx";
import { AdjustMuscleGroupWeightsDialog } from "./dialogs/AdjustMuscleGroupWeightsDialog.tsx";
import { FriendRequestDialog } from "./dialogs/FriendRequestDialog.tsx";
import { ShareRoutineDialog } from "./dialogs/ShareRoutineDialog.tsx";

type DialogType =
  | "start-workout"
  | "record-workout"
  | "edit-workout"
  | "add-set"
  | "edit-set"
  | "create-exercise"
  | "edit-exercise"
  | "merge-exercises"
  | "create-routine"
  | "edit-routine"
  | "onboarding"
  | "profile-settings"
  | "adjust-muscle-group-weights"
  | "send-friend-request"
  | "share-workout-routine"
  | "confirm"
  | null;

interface DialogStackItem {
  type: DialogType;
  props: any;
}

interface DialogContextValue {
  openDialog: (type: DialogType, props?: any) => void;
  closeDialog: () => void;
  currentDialog: DialogType;
  error: Error | null;
  setError: React.Dispatch<React.SetStateAction<Error | null>>;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within DialogProvider");
  }
  return context;
};

interface OnboardingContextValue {
  step: OnboardingDialogStep;
  setStep: React.Dispatch<React.SetStateAction<OnboardingDialogStep>>;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};

interface DialogProviderProps {
  profile?: User;
  children: ReactNode;
}

export const DialogProvider: React.FC<DialogProviderProps> = ({
  profile,
  children,
}) => {
  const [dialogStack, setDialogStack] = useState<DialogStackItem[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [onboardingStep, setOnboardingStep] = useState<OnboardingDialogStep>(0);

  const currentDialog =
    dialogStack.length > 0 ? dialogStack[dialogStack.length - 1].type : null;

  // Disable background scroll if dialog is open
  useEffect(() => {
    const body = document.querySelector("body");

    if (!body) {
      return;
    }

    body.style.overflow = currentDialog ? "hidden" : "auto";
  }, [currentDialog]);

  const openDialog = React.useCallback((type: DialogType, props?: any) => {
    setDialogStack((prev) => [...prev, { type, props: props || null }]);
  }, []);

  const closeDialog = React.useCallback(() => {
    setError(null);
    setDialogStack((prev) => {
      if (prev.length > 0) {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);

  // Clear error when closing the last dialog
  React.useEffect(() => {
    if (dialogStack.length === 0) {
      setError(null);
    }
  }, [dialogStack.length]);

  return (
    <OnboardingContext.Provider
      value={{ step: onboardingStep, setStep: setOnboardingStep }}
    >
      <DialogContext.Provider
        value={{ openDialog, closeDialog, currentDialog, error, setError }}
      >
        {children}
        {dialogStack.map((item, index) => {
          const isTopDialog = index === dialogStack.length - 1;

          if (item.type === "record-workout" || item.type === "edit-workout") {
            return (
              <WorkoutDialog
                key={`workout-${index}`}
                open={isTopDialog}
                closeDialog={closeDialog}
                workout={item.props?.workout}
                preferredResistanceUnit={
                  profile?.preferred_resistance_unit || "kg"
                }
                preferredVisibility={profile?.preferred_visibility || "private"}
                preferredTimeFormat={profile?.preferred_time_format || "24h"}
                exercises={item.props.exercises}
              />
            );
          }

          if (item.type === "add-set" || item.type === "edit-set") {
            return (
              <SetDialog
                key={`set-${index}`}
                open={isTopDialog}
                closeDialog={closeDialog}
                initialRepetitions={item.props?.repetitions}
                initialResistance={item.props?.resistance}
                resistanceUnit={item.props?.resistanceUnit}
                exercise={item.props?.exercise}
                editMode={item.type === "edit-set"}
                onAddSet={item.props?.onAddSet}
                onEditSet={item.props?.onEditSet}
              />
            );
          }

          if (item.type === "create-routine" || item.type === "edit-routine") {
            return (
              <RoutineDialog
                key={`routine-${index}`}
                open={isTopDialog}
                closeDialog={closeDialog}
                routine={item.props?.routine}
                exercises={item.props.exercises}
              />
            );
          }

          if (
            item.type === "create-exercise" ||
            item.type === "edit-exercise"
          ) {
            return (
              <ExerciseDialog
                key={`exercise-${index}`}
                open={isTopDialog}
                closeDialog={closeDialog}
                exercise={item.props?.exercise}
                preferredResistanceUnit={
                  item.props?.exercise?.default_resistance_unit ||
                  profile?.preferred_resistance_unit ||
                  "kg"
                }
              />
            );
          }

          if (item.type === "merge-exercises") {
            return (
              <MergeExerciseDialog
                key={`merge-exercises-${index}`}
                open={isTopDialog}
                onClose={() => closeDialog()}
                exercise1={item.props.exercise1}
                exercise2={item.props.exercise2}
              />
            );
          }

          if (item.type === "onboarding") {
            return (
              <OnboardingDialog
                key={`onboarding-${index}`}
                open={isTopDialog}
              />
            );
          }

          if (item.type === "profile-settings") {
            return (
              <ProfileDialog
                key={`profile-${index}`}
                open={isTopDialog}
                closeDialog={closeDialog}
                profile={profile}
              />
            );
          }

          if (item.type === "adjust-muscle-group-weights") {
            return (
              <AdjustMuscleGroupWeightsDialog
                open={isTopDialog}
                closeDialog={closeDialog}
                exercise={item.props.exercise}
                primaryMuscleGroups={item.props.primaryMuscleGroups}
                secondaryMuscleGroups={item.props.secondaryMuscleGroups}
                onSave={item.props.onSave}
              />
            );
          }

          if (item.type === "send-friend-request") {
            return (
              <FriendRequestDialog
                open={isTopDialog}
                closeDialog={closeDialog}
                profile={profile}
                friends={item.props.friends}
                requests={item.props.requests}
                outgoing={item.props.outgoing}
              />
            );
          }

          if (item.type === "share-workout-routine") {
            return (
              <ShareRoutineDialog
                open={isTopDialog}
                closeDialog={closeDialog}
                routine={item.props.routine}
                friends={item.props.friends}
              />
            );
          }

          if (item.type === "confirm") {
            return (
              <ConfirmDialog
                open={isTopDialog}
                onClose={closeDialog}
                onConfirm={item.props.onConfirm}
                title={item.props.title}
                message={item.props.message}
                infoMessage={item.props.infoMessage}
                warningMessage={item.props.warningMessage}
                dangerMessage={item.props.dangerMessage}
                confirmText={item.props.confirmText}
                cancelText={item.props.cancelText}
                variant={item.props.variant}
              />
            );
          }

          return null;
        })}
      </DialogContext.Provider>
    </OnboardingContext.Provider>
  );
};
